import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Input, Select } from '../ui';
import { maskCEP, maskPhone } from '../../utils/masks';
import { Address } from '../../types';

const BRAZILIAN_STATES = [
  { value: 'AC', label: 'Acre' },
  { value: 'AL', label: 'Alagoas' },
  { value: 'AP', label: 'Amapá' },
  { value: 'AM', label: 'Amazonas' },
  { value: 'BA', label: 'Bahia' },
  { value: 'CE', label: 'Ceará' },
  { value: 'DF', label: 'Distrito Federal' },
  { value: 'ES', label: 'Espírito Santo' },
  { value: 'GO', label: 'Goiás' },
  { value: 'MA', label: 'Maranhão' },
  { value: 'MT', label: 'Mato Grosso' },
  { value: 'MS', label: 'Mato Grosso do Sul' },
  { value: 'MG', label: 'Minas Gerais' },
  { value: 'PA', label: 'Pará' },
  { value: 'PB', label: 'Paraíba' },
  { value: 'PR', label: 'Paraná' },
  { value: 'PE', label: 'Pernambuco' },
  { value: 'PI', label: 'Piauí' },
  { value: 'RJ', label: 'Rio de Janeiro' },
  { value: 'RN', label: 'Rio Grande do Norte' },
  { value: 'RS', label: 'Rio Grande do Sul' },
  { value: 'RO', label: 'Rondônia' },
  { value: 'RR', label: 'Roraima' },
  { value: 'SC', label: 'Santa Catarina' },
  { value: 'SP', label: 'São Paulo' },
  { value: 'SE', label: 'Sergipe' },
  { value: 'TO', label: 'Tocantins' },
];

export interface AddressFormProps {
  data: Partial<Address>;
  onChange: (data: Partial<Address>) => void;
  errors?: Partial<Record<keyof Address, string>>;
  showPhones?: boolean;
  showPoBox?: boolean;
  disabled?: boolean;
}

export const AddressForm: React.FC<AddressFormProps> = ({
  data,
  onChange,
  errors = {},
  showPhones = true,
  showPoBox = false,
  disabled = false,
}) => {
  const { t } = useTranslation();

  const handleChange = useCallback(
    (field: keyof Address) => (value: string) => {
      onChange({ ...data, [field]: value });
    },
    [data, onChange]
  );

  const handleCEPChange = useCallback(
    async (value: string) => {
      const maskedValue = maskCEP(value);
      onChange({ ...data, zipCode: maskedValue });

      // Auto-complete via ViaCEP API when CEP is complete
      const cleanCEP = maskedValue.replace(/\D/g, '');
      if (cleanCEP.length === 8) {
        try {
          const response = await fetch(`https://viacep.com.br/ws/${cleanCEP}/json/`);
          const cepData = await response.json();

          if (!cepData.erro) {
            onChange({
              ...data,
              zipCode: maskedValue,
              street: cepData.logradouro || data.street,
              neighborhood: cepData.bairro || data.neighborhood,
              city: cepData.localidade || data.city,
              state: cepData.uf || data.state,
            });
          }
        } catch (error) {
          console.error('Erro ao consultar CEP:', error);
        }
      }
    },
    [data, onChange]
  );

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Input
          label={t('address.zipCode')}
          value={data.zipCode || ''}
          onChange={handleCEPChange}
          mask={maskCEP}
          placeholder="00000-000"
          error={errors.zipCode}
          required
          disabled={disabled}
        />

        <div className="md:col-span-2">
          <Input
            label={t('address.street')}
            value={data.street || ''}
            onChange={handleChange('street')}
            placeholder={t('address.streetPlaceholder')}
            error={errors.street}
            required
            disabled={disabled}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Input
          label={t('address.number')}
          value={data.number || ''}
          onChange={handleChange('number')}
          placeholder={t('address.numberPlaceholder')}
          error={errors.number}
          required
          disabled={disabled}
        />

        <div className="md:col-span-3">
          <Input
            label={t('address.complement')}
            value={data.complement || ''}
            onChange={handleChange('complement')}
            placeholder={t('address.complementPlaceholder')}
            error={errors.complement}
            disabled={disabled}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Input
          label={t('address.neighborhood')}
          value={data.neighborhood || ''}
          onChange={handleChange('neighborhood')}
          placeholder={t('address.neighborhood')}
          error={errors.neighborhood}
          required
          disabled={disabled}
        />

        <Input
          label={t('address.city')}
          value={data.city || ''}
          onChange={handleChange('city')}
          placeholder={t('address.city')}
          error={errors.city}
          required
          disabled={disabled}
        />

        <Select
          label={t('address.state')}
          value={data.state || ''}
          onChange={handleChange('state')}
          options={BRAZILIAN_STATES}
          placeholder={t('address.statePlaceholder')}
          error={errors.state}
          required
          disabled={disabled}
        />
      </div>

      {showPoBox && (
        <Input
          label={t('address.poBox')}
          value={data.poBox || ''}
          onChange={handleChange('poBox')}
          placeholder={t('address.poBox')}
          error={errors.poBox}
          disabled={disabled}
        />
      )}

      {showPhones && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label={t('address.homePhone')}
            value={data.homePhone || ''}
            onChange={handleChange('homePhone')}
            mask={maskPhone}
            placeholder="(00) 0000-0000"
            error={errors.homePhone}
            disabled={disabled}
          />

          <Input
            label={t('address.mobilePhone')}
            value={data.mobilePhone || ''}
            onChange={handleChange('mobilePhone')}
            mask={maskPhone}
            placeholder="(00) 00000-0000"
            error={errors.mobilePhone}
            required
            disabled={disabled}
          />
        </div>
      )}
    </div>
  );
};

export default AddressForm;
